import type { Topic, GapSignal, ErrorClassification } from "./types";

export type SocraticExchange = {
  id: string;
  topicId: string;
  conceptId?: string;
  studentMessage: string;
  socraticQuestion: string;
  studentResponse?: string;
  misconceptionExposed: boolean;
  createdAt: string;
};

export type SocraticPrompt = {
  question: string;
  type: "probing" | "clarifying" | "challenge" | "evidence" | "implication" | "general";
  purpose: string;
  followUp?: string[];
};

export type DialogueStrategy = {
  errorType: ErrorClassification;
  primaryQuestion: SocraticPrompt;
  followUpQuestions: SocraticPrompt[];
  escalationThreshold: number;
};

const SOCRATIC_TEMPLATES: Record<ErrorClassification, DialogueStrategy> = {
  conceptual_misunderstanding: {
    errorType: "conceptual_misunderstanding",
    primaryQuestion: {
      question: "Can you explain what you understand by that concept?",
      type: "clarifying",
      purpose: "Understand the student's current mental model",
      followUp: [
        "Where did you learn that?",
        "How does that connect to what we discussed before?"
      ]
    },
    followUpQuestions: [
      {
        question: "What would happen if that weren't true?",
        type: "implication",
        purpose: "Test the depth of the misconception"
      },
      {
        question: "Can you give me an example of what you mean?",
        type: "probing",
        purpose: "Surface the underlying assumption"
      },
      {
        question: "What evidence supports your thinking?",
        type: "evidence",
        purpose: "Check for fact-based reasoning"
      }
    ],
    escalationThreshold: 2
  },
  
  factual_error: {
    errorType: "factual_error",
    primaryQuestion: {
      question: "What do you remember about this topic from your studies?",
      type: "probing",
      purpose: "Understand what information the student has",
      followUp: [
        "Can you recall where you read or heard that?",
        "What else do you remember about this?"
      ]
    },
    followUpQuestions: [
      {
        question: "Is there anything in the source material that might help clarify this?",
        type: "evidence",
        purpose: "Redirect to authoritative sources"
      },
      {
        question: "How confident are you about that?",
        type: "clarifying",
        purpose: "Assess certainty level"
      }
    ],
    escalationThreshold: 1
  },
  
  procedural_mistake: {
    errorType: "procedural_mistake",
    primaryQuestion: {
      question: "Walk me through the steps you took to get there.",
      type: "probing",
      purpose: "Understand the student's process",
      followUp: [
        "What happened after that step?",
        "Why did you choose that approach?"
      ]
    },
    followUpQuestions: [
      {
        question: "What do you think went wrong?",
        type: "challenge",
        purpose: "Self-reflection on error"
      },
      {
        question: "Is there another way to approach this?",
        type: "implication",
        purpose: "Open alternative pathways"
      }
    ],
    escalationThreshold: 2
  },
  
  vocabulary_confusion: {
    errorType: "vocabulary_confusion",
    primaryQuestion: {
      question: "What do you think the word '{term}' means?",
      type: "clarifying",
      purpose: "Understand the student's definition",
      followUp: [
        "Where have you seen that word before?",
        "How did you figure out its meaning?"
      ]
    },
    followUpQuestions: [
      {
        question: "Does the context help you understand it differently?",
        type: "evidence",
        purpose: "Use contextual clues"
      },
      {
        question: "What other words might mean something similar?",
        type: "probing",
        purpose: "Build semantic network"
      }
    ],
    escalationThreshold: 2
  },
  
  reasoning_gap: {
    errorType: "reasoning_gap",
    primaryQuestion: {
      question: "Why do you think that's the answer?",
      type: "evidence",
      purpose: "Understand the student's reasoning chain",
      followUp: [
        "What evidence supports that?",
        "How did you connect those ideas?"
      ]
    },
    followUpQuestions: [
      {
        question: "What would you expect to see if that were true?",
        type: "implication",
        purpose: "Test logical consistency"
      },
      {
        question: "Is there another way to look at this problem?",
        type: "challenge",
        purpose: "Expand perspective"
      },
      {
        question: "What if we changed one thing—what would happen then?",
        type: "implication",
        purpose: "Explore variables"
      }
    ],
    escalationThreshold: 3
  },
  
  syntax_error: {
    errorType: "syntax_error",
    primaryQuestion: {
      question: "Read your response aloud. Do you notice anything?",
      type: "clarifying",
      purpose: "Self-detection of errors",
      followUp: [
        "How does it sound when you say it?",
        "Is there a pattern you recognize?"
      ]
    },
    followUpQuestions: [
      {
        question: "What rule do we follow for that?",
        type: "evidence",
        purpose: "Recall the rule"
      },
      {
        question: "Can you compare it to a correct example?",
        type: "probing",
        purpose: "Pattern recognition"
      }
    ],
    escalationThreshold: 2
  },
  
  comprehension_failure: {
    errorType: "comprehension_failure",
    primaryQuestion: {
      question: "What was the main point of the passage you just read?",
      type: "clarifying",
      purpose: "Assess literal comprehension",
      followUp: [
        "What happened in the text?",
        "Who was involved?"
      ]
    },
    followUpQuestions: [
      {
        question: "What do you think the author wanted you to understand?",
        type: "implication",
        purpose: "Assess author's intent"
      },
      {
        question: "Were there any words that made it hard to understand?",
        type: "evidence",
        purpose: "Identify vocabulary gaps"
      },
      {
        question: "What questions do you still have after reading?",
        type: "probing",
        purpose: "Surface confusion"
      }
    ],
    escalationThreshold: 2
  },
  
  no_error: {
    errorType: "no_error",
    primaryQuestion: {
      question: "Can you explain how you arrived at that answer?",
      type: "probing",
      purpose: "Reinforce correct reasoning",
      followUp: [
        "What clues helped you?",
        "How would you explain this to a friend?"
      ]
    },
    followUpQuestions: [
      {
        question: "What made this approach effective?",
        type: "evidence",
        purpose: "Consolidate learning"
      }
    ],
    escalationThreshold: 0
  }
};

export function generateSocraticQuestion(
  errorType: ErrorClassification,
  context?: {
    topicName?: string;
    specificTerm?: string;
    previousQuestion?: string;
    attemptCount?: number;
  }
): SocraticPrompt {
  const strategy = SOCRATIC_TEMPLATES[errorType];
  
  let question = strategy.primaryQuestion.question;
  
  if (context?.specificTerm) {
    question = question.replace("{term}", context.specificTerm);
  }
  
  if (context?.topicName && !question.includes(context.topicName)) {
    question = `${context.topicName}: ${question}`;
  }
  
  const questionIndex = Math.min(
    Math.floor((context?.attemptCount || 0) / 2),
    strategy.followUpQuestions.length
  );
  
  if (questionIndex > 0 && questionIndex <= strategy.followUpQuestions.length) {
    const followUp = strategy.followUpQuestions[questionIndex - 1];
    return {
      question,
      type: strategy.primaryQuestion.type,
      purpose: strategy.primaryQuestion.purpose,
      followUp: followUp ? [followUp.question] : strategy.primaryQuestion.followUp
    };
  }
  
  return {
    question,
    type: strategy.primaryQuestion.type,
    purpose: strategy.primaryQuestion.purpose,
    followUp: strategy.primaryQuestion.followUp
  };
}

export function generateSocraticDialogue(
  gapSignal: GapSignal,
  topic: Topic,
  exchangeHistory: SocraticExchange[] = []
): SocraticExchange {
  const topicExchanges = exchangeHistory.filter(e => e.topicId === topic.id);
  const attemptCount = topicExchanges.length;
  
  const errorType = mapGapToErrorType(gapSignal);
  const context = {
    topicName: topic.name,
    previousQuestion: topicExchanges[topicExchanges.length - 1]?.socraticQuestion,
    attemptCount
  };
  
  const prompt = generateSocraticQuestion(errorType, context);
  
  return {
    id: `socratic-${topic.id}-${Date.now()}`,
    topicId: topic.id,
    conceptId: gapSignal.conceptId,
    studentMessage: gapSignal.evidence,
    socraticQuestion: prompt.question,
    misconceptionExposed: false,
    createdAt: new Date().toISOString()
  };
}

function mapGapToErrorType(gapSignal: GapSignal): ErrorClassification {
  if (gapSignal.signalSource === "chat") {
    if (gapSignal.evidence.includes("reasoning")) return "reasoning_gap";
    if (gapSignal.evidence.includes("concept")) return "conceptual_misunderstanding";
    if (gapSignal.evidence.includes("vocabulary") || gapSignal.evidence.includes("word")) {
      return "vocabulary_confusion";
    }
    if (gapSignal.evidence.includes("sentence") || gapSignal.evidence.includes("grammar")) {
      return "syntax_error";
    }
    if (gapSignal.evidence.includes("read") || gapSignal.evidence.includes("passage")) {
      return "comprehension_failure";
    }
  }
  
  if (gapSignal.signalSource === "quiz") {
    if (gapSignal.evidence.includes("wrong")) return "procedural_mistake";
    if (gapSignal.evidence.includes("remember")) return "factual_error";
  }
  
  return "conceptual_misunderstanding";
}

export function analyzeStudentResponse(
  response: string,
  socraticQuestion: SocraticPrompt,
  correctAnswer?: string
): {
  misconceptionExposed: boolean;
  understandingImproved: boolean;
  needsEscalation: boolean;
  insight: string;
} {
  const lowerResponse = response.toLowerCase();
  const questionLower = socraticQuestion.question.toLowerCase();
  
  const positiveIndicators = [
    "because", "therefore", "so", "which means", "this shows",
    "i understand", "i see", "that makes sense", "oh",
    "i learned", "i realized"
  ];
  
  const negativeIndicators = [
    "i don't know", "not sure", "confused", "lost",
    "still don't", "still unclear", "baffled"
  ];
  
  const misconceptionIndicators = [
    "i thought", "i believed", "i assumed", "maybe",
    "i guessed", "i wasn't sure"
  ];
  
  const positiveCount = positiveIndicators.filter(i => lowerResponse.includes(i)).length;
  const negativeCount = negativeIndicators.filter(i => lowerResponse.includes(i)).length;
  const misconceptionCount = misconceptionIndicators.filter(i => lowerResponse.includes(i)).length;
  
  const misconceptionExposed = misconceptionCount > 0 && positiveCount === 0;
  const understandingImproved = positiveCount > negativeCount;
  
  const strategy = Object.values(SOCRATIC_TEMPLATES).find(
    s => s.primaryQuestion.question === socraticQuestion.question ||
         s.primaryQuestion.followUp?.includes(socraticQuestion.question)
  );
  
  const needsEscalation = misconceptionExposed || 
    (negativeCount > 1) ||
    (understandingImproved && socraticQuestion.type === "clarifying");
  
  let insight = "";
  if (misconceptionExposed) {
    insight = "Student's misconception has been exposed. This is valuable for targeted remediation.";
  } else if (understandingImproved) {
    insight = "Student demonstrated improved understanding through their response.";
  } else if (negativeCount > 0) {
    insight = "Student still has questions. Consider providing more scaffolding.";
  } else {
    insight = "Student response requires further analysis.";
  }
  
  return {
    misconceptionExposed,
    understandingImproved,
    needsEscalation,
    insight
  };
}

export function selectNextDialogueTopic(
  gapSignals: GapSignal[],
  topics: Topic[],
  masteryGaps: Array<{ topicId: string; mastery: number }>
): {
  topic: Topic;
  reason: string;
  priority: number;
} | null {
  const topicGaps = gapSignals.filter(s => s.signalSource === "chat" || s.signalSource === "interpretation");
  
  if (topicGaps.length === 0) return null;
  
  const groupedByTopic = new Map<string, GapSignal[]>();
  for (const signal of topicGaps) {
    const existing = groupedByTopic.get(signal.topicId) || [];
    existing.push(signal);
    groupedByTopic.set(signal.topicId, existing);
  }
  
  let bestTopic: Topic | null = null;
  let bestScore = -1;
  let bestReason = "";
  
  for (const [topicId, signals] of groupedByTopic) {
    const topic = topics.find(t => t.id === topicId);
    if (!topic) continue;
    
    const mastery = masteryGaps.find(m => m.topicId === topicId);
    const masteryScore = mastery ? (1 - mastery.mastery) : 0.5;
    
    const severityScore = Math.max(...signals.map(s => 
      s.severity === "high" ? 1 : s.severity === "medium" ? 0.5 : 0.2
    ));
    
    const score = severityScore * 0.6 + masteryScore * 0.4;
    
    if (score > bestScore) {
      bestScore = score;
      bestTopic = topic;
      bestReason = `Multiple gaps detected (${signals.length}) with ${signals[0].severity} severity`;
    }
  }
  
  if (!bestTopic) return null;
  
  return {
    topic: bestTopic,
    reason: bestReason,
    priority: bestScore
  };
}

export function buildSocraticPromptForKnowledgeCore(
  topic: Topic,
  studentQuestion: string,
  gapSignal?: GapSignal
): string {
  const strategy = gapSignal 
    ? SOCRATIC_TEMPLATES[mapGapToErrorType(gapSignal)]
    : SOCRATIC_TEMPLATES.no_error;
  
  let prompt = `Student Question: "${studentQuestion}"\n\n`;
  prompt += `Context: The student is working on "${topic.name}"\n`;
  prompt += `Topic description: ${topic.description}\n`;
  
  if (gapSignal) {
    prompt += `\nGap detected: ${gapSignal.evidence}\n`;
    prompt += `Severity: ${gapSignal.severity}\n`;
  }
  
  prompt += `\n`;
  prompt += `Generate a Socratic question that:\n`;
  prompt += `1. Helps the student discover their misunderstanding\n`;
  prompt += `2. Is concise (1-2 sentences)\n`;
  prompt += `3. Encourages deeper thinking without giving away the answer\n`;
  prompt += `4. Is appropriate for the topic: ${topic.name}\n`;
  prompt += `\n`;
  prompt += `Question type: ${strategy.primaryQuestion.type}\n`;
  prompt += `Purpose: ${strategy.primaryQuestion.purpose}\n`;
  
  return prompt;
}

export function shouldEscalateToDirectInstruction(
  exchangeHistory: SocraticExchange[],
  threshold: number = 3
): {
  shouldEscalate: boolean;
  reason: string;
  recommendation: string;
} {
  const recentExchanges = exchangeHistory.slice(-5);
  const misconceptionsExposed = recentExchanges.filter(e => e.misconceptionExposed).length;
  const withoutResponse = recentExchanges.filter(e => !e.studentResponse).length;
  
  if (misconceptionsExposed >= threshold) {
    return {
      shouldEscalate: true,
      reason: `Student has exposed ${misconceptionsExposed} misconceptions without resolution`,
      recommendation: "Consider providing direct instruction or a targeted resource to address the persistent misunderstanding."
    };
  }
  
  if (withoutResponse >= threshold) {
    return {
      shouldEscalate: true,
      reason: "Student has not engaged with the last few Socratic questions",
      recommendation: "The student may need a different approach. Consider providing more direct guidance."
    };
  }
  
  return {
    shouldEscalate: false,
    reason: "Dialogue is still productive",
    recommendation: "Continue with Socratic questioning"
  };
}
